/**
 * The shared game card bubble (R8): a frozen snapshot of a game at the
 * moment it was shared, never re-fetched or re-rendered against live state
 * (`captureGameCardSnapshot`'s own comment: "does not need to keep
 * updating... just left as is at the time it was shared"). The whole card
 * is one tap target that opens the live game.
 *
 * `cards` holds one entry per real `gameCard` message, folded together by
 * `conversationListState.ts`'s `foldConsecutiveGameCardRows` when two or
 * three were shared back to back by the same sender -- see that function's
 * own doc comment for why the fold happens client side rather than as a
 * single message with several cards.
 *
 * Team marks reuse `gameDetail/TeamMark.tsx` rather than a second
 * abbreviation-plate component: every other screen in the product (
 * `ScoreRow`, `GameDetailHeader`) renders a team the same way, ignoring
 * `logoMediaId` in favor of the abbreviation plate, and this bubble matches
 * that rather than being the first place on the platform to resolve a real
 * team logo image.
 */
import { cn } from "@sportsmet/ui";
import { useNavigate } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { TeamMark } from "../gameDetail/TeamMark";
import { resolvePlatePair } from "../gameDetail/teamPlateText";
import { GAME_CARD_SHARED_CAPTION } from "./chatExtrasCopy";
import type { typeMessageGameCard } from "./mergeMessagePages";
import { formatMessageTime } from "./relativeTime";

export type typeGameCardBubbleProps = {
  cards: typeMessageGameCard[];
};

function TeamLine(props: {
  team: typeMessageGameCard["home"];
  /** The plate text after the two sides of THIS card have been compared
   *  (ISSUE-902 R4), not the team's raw abbreviation. A shared card draws
   *  both teams of one game, so two teams that carry the same abbreviation
   *  would otherwise sit one above the other as identical plates. */
  plateText: string;
}): ReactNode {
  return (
    <div className="flex items-center gap-2">
      <TeamMark
        name={props.team.name}
        abbreviation={props.plateText}
        size="sm"
      />
      <span className="min-w-0 flex-1 truncate text-body text-ink">
        {props.team.name}
      </span>
      <span className="font-mono text-score tabular-nums text-ink">
        {props.team.score ?? ""}
      </span>
    </div>
  );
}

function OneCard(props: { card: typeMessageGameCard }): ReactNode {
  const { card } = props;
  const navigate = useNavigate();
  const statusLine =
    card.kickoffAtIso != null && card.kickoffAtIso !== ""
      ? `${card.statusLabel} · ${formatMessageTime(card.kickoffAtIso)}`
      : card.statusLabel;
  // Resolved per card, not per bubble: a folded bubble holds up to three
  // different games and the rule compares the two sides of one game only.
  // The snapshot this card froze is the right input, the same as every other
  // value drawn here, so an abbreviation the catalog changed later does not
  // move a plate that was already shared.
  const { awayText, homeText } = resolvePlatePair({
    away: { name: card.away.name, abbreviation: card.away.abbreviation },
    home: { name: card.home.name, abbreviation: card.home.abbreviation },
  });

  return (
    <button
      type="button"
      data-testid="game-card"
      data-event-id={card.eventId}
      onClick={() => {
        void navigate({ to: "/scores", search: { eventId: card.eventId } });
      }}
      className={cn(
        "flex w-full flex-col gap-2 rounded-md border border-hairline bg-surface p-3 text-left",
        "hover:bg-surface-sunken",
      )}
    >
      <span className="text-caption text-ink-tertiary">{card.leagueName}</span>
      <TeamLine team={card.away} plateText={awayText} />
      <TeamLine team={card.home} plateText={homeText} />
      <span
        className="text-caption text-ink-secondary"
        data-testid="game-card-status"
      >
        {statusLine}
      </span>
    </button>
  );
}

export const GameCardBubble = (props: typeGameCardBubbleProps): ReactNode => {
  const { cards } = props;

  if (cards.length === 0) return null;

  return (
    <div className="flex flex-col gap-1" data-testid="game-card-bubble">
      {/* A stack for two or three; a single card at length 1 is the same
       *  layout with one child, never a special case. */}
      <div className="flex flex-col gap-2">
        {cards.map((card) => (
          <OneCard key={card.eventId} card={card} />
        ))}
      </div>
      <span className="text-caption text-ink-tertiary">
        {GAME_CARD_SHARED_CAPTION}
      </span>
    </div>
  );
};
